/**
 * 계산기 랜딩의 접이식 설명 섹션.
 *
 * 도구 페이지는 본문이 얇아 검색에서 "내용 없는 페이지"로 취급되기 쉽다.
 * 그렇다고 긴 글을 펼쳐두면 정작 시작 버튼을 누르지 않는다.
 *
 * 그래서 기본은 접어둔다 — 읽고 싶은 사람만 펼치고, 크롤러는 접혀 있어도 읽는다.
 * 같은 내용을 FAQPage 구조화 데이터로도 내보낸다. (구글 FAQ 리치 결과는 2026-05-07 종료 —
 * 마크업은 해가 없어 유지하고, 효과는 화면 본문에서 나온다. structured-data-spec §5.2)
 * 화면 문구와 구조화 데이터가 어긋나지 않도록 **한 배열에서 둘 다 만든다.**
 *
 * 기획: docs/plan/2026-08-25-carecalc-seo-plan.md
 */
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { FAQPageJsonLd } from '@/components/common/JsonLd'

/**
 * 연도마다 바뀌는 금액(월 한도액 등)은 적지 않는다. 하드코딩하면 해가 바뀔 때
 * 반드시 틀어지고, 틀린 금액은 없는 것만 못하다. 본인부담률처럼 제도가 바뀌어야
 * 움직이는 값만 쓴다. 구체적인 금액은 계산기가 답한다.
 */
const FAQS = [
  {
    question: '장기요양등급은 어떻게 나뉘나요?',
    answer:
      '1등급부터 5등급, 그리고 인지지원등급으로 나뉩니다. 1등급이 가장 도움이 많이 필요한 상태이고, ' +
      '숫자가 커질수록 스스로 하실 수 있는 일이 많다는 뜻입니다. 치매가 있으시면 신체 기능이 ' +
      '비교적 괜찮아도 인지지원등급을 받으실 수 있습니다. 등급에 따라 이용할 수 있는 서비스와 ' +
      '지원 금액이 달라집니다.',
  },
  {
    question: '등급은 누가, 어떻게 정하나요?',
    answer:
      '국민건강보험공단에 신청하시면 직원이 댁으로 방문해 조사합니다. 이때 쓰는 것이 인정조사표 52개 ' +
      '항목입니다. 몸을 움직이시는 정도, 기억과 판단, 행동 변화, 필요한 의료 처치, 재활 상태를 봅니다. ' +
      '여기에 의사 소견서를 더해 등급판정위원회가 최종 등급을 정합니다. 이 계산기는 같은 52개 항목을 ' +
      '기준으로 계산하지만, 방문조사와 소견서는 반영할 수 없어 결과가 구간으로 나옵니다.',
  },
  {
    question: '요양원에 모시면 한 달에 얼마나 드나요?',
    answer:
      '등급을 받으시면 요양원(시설급여) 이용료의 80%를 공단이 부담하고 20%를 본인이 부담합니다. ' +
      '여기에 식비, 간식비, 이미용비 같은 비급여 항목이 더해집니다. 비급여는 전액 본인 부담이고 ' +
      '시설마다 다릅니다. 집에서 방문요양을 받으시는 경우(재가급여)는 본인부담이 15%이고, ' +
      '등급별 월 한도액을 넘겨 이용하시면 넘긴 만큼은 전액 본인 부담입니다.',
  },
  {
    question: '본인부담금을 덜 낼 수 있는 경우가 있나요?',
    answer:
      '있습니다. 기초생활수급자로 의료급여를 받으시면 본인부담금이 면제됩니다. ' +
      '기초생활수급자는 아니지만 의료급여를 받으시는 분(국가유공자·북한이탈주민 등), ' +
      '차상위계층으로 병원비 경감을 받고 계신 분, 건강보험료가 하위 25% 안에 드시는 분은 60% 감경되어 ' +
      '요양원은 8%, 집에서 받는 서비스는 6%를 내십니다. 건강보험료가 하위 25~50% 사이면 40% 감경되어 ' +
      '요양원 12%, 집에서 받는 서비스 9%입니다. 다만 식비 같은 비급여는 감경 대상이셔도 전액 본인 ' +
      '부담입니다. 계산기에서 해당 여부를 고르시면 감경을 반영한 금액으로 계산해 드립니다.',
  },
  {
    question: '이 계산기 결과를 그대로 믿어도 되나요?',
    answer:
      '참고용으로 보셔야 합니다. 실제 등급은 공단 직원의 방문조사와 의사 소견서까지 보고 정해지는데, ' +
      '이 계산기는 답변만 가지고 계산합니다. 그래서 하나의 등급이 아니라 인접한 두 등급의 구간으로 ' +
      '보여드립니다. 신청 전에 대략의 상황을 가늠하시는 용도로 쓰시고, 정확한 등급은 공단에 ' +
      '신청하셔서 확인하시는 것이 맞습니다.',
  },
]

export default function CareCalcFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section aria-label="장기요양등급과 비용 안내">
      <FAQPageJsonLd faqs={FAQS} />

      <h2 className="mb-3 text-body font-bold text-cb-gray-900">
        장기요양등급과 비용, 이것만 알아두세요
      </h2>

      <div className="divide-y divide-cb-gray-200 overflow-hidden rounded-xl border border-cb-gray-200">
        {FAQS.map((faq, i) => {
          const open = openIndex === i
          return (
            <div key={faq.question}>
              <button
                type="button"
                onClick={() => setOpenIndex(open ? null : i)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
              >
                <span className="text-body-sm font-medium text-cb-gray-800">{faq.question}</span>
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 text-cb-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </button>
              {/* 접혀 있을 때도 DOM 에 남긴다 — 숨기는 게 아니라 접는 것이고, 크롤러가 읽어야 한다 */}
              <div className={open ? 'px-4 pb-4' : 'hidden'}>
                <p className="text-body-sm leading-relaxed text-cb-gray-600">{faq.answer}</p>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
